import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';
import { stringify } from 'qs';
import { useCallback } from 'react';
import { useModal } from 'yoso';
import { SmsVerificationPopup } from '~/components/Adventure/Popup/SmsVerificationPopup/SmsVerificationPopup';
import { useIsMobile } from '~/components/Breakpoint/Breakpoint.hook';
import * as vault from '~/components/Dosi/Vault';
import { BaseError } from '~/components/Error/BaseError';
import { useDefaultErrorHandler } from '~/components/Error/Error.hooks';
import { useMember } from '~/components/Member/Member.hooks';
import { UseDosiminMutationOptions } from '~/components/Query/Query.hooks';
import { openPopupTopRight } from '~/components/Util';

const DOSI_MEMBERS_URL = import.meta.env.VITE_DOSI_MEMBERS_URL;

export type LoginOptions = {
  returnTargetURI?: string;
  referralCode?: string;
};

export const authVerifyUrl = `${document.location.origin}/auth/verify`;

export const setReferralCodeToUri = (uri: string, referralCode: string) => {
  if (!uri) return '';
  const uriObject = new URL(uri);
  const targetUri = uriObject.searchParams.get('returnUri');
  const targetUriObject = new URL(targetUri!);
  targetUriObject.searchParams.set('referralCode', referralCode);
  uriObject.searchParams.set('returnUri', targetUriObject.toString());
  return uriObject.toString();
};

export const useLogin = ({
  returnTargetURI = location.pathname,
  referralCode,
}: LoginOptions = {}) => {
  const {
    data: { loginUri },
  } = useMember({ useErrorBoundary: false });
  const isMobile = useIsMobile();
  return useCallback(async () => {
    localStorage.setItem('RETURN_TARGET_URI', returnTargetURI);
    const targetUri = referralCode ? setReferralCodeToUri(loginUri!, referralCode) : loginUri!;

    if (vault.agent) {
      await vault.act('doLogin');
      document.location.reload();
    } else if (isMobile) {
      window.location.href = targetUri;
    } else {
      openPopupTopRight(targetUri, 'WALLET_POPUP', {
        width: 375,
        height: 609,
      });
    }
  }, [isMobile, loginUri, referralCode, returnTargetURI]);
};

export const authLogoutUrl = `${document.location.origin}/auth/logout`;

export const useLogout = () =>
  useCallback(() => {
    const dosiLogoutUrl = `${DOSI_MEMBERS_URL}/api/v2/sso/sign-out?${stringify({
      redirect_uri: location.origin,
    })}`;

    window.location.href = dosiLogoutUrl;
  }, []);

export const finishLoginUrl = '/api/citizen/v1/login/finish';
export const finishLogin = () => axios.post(finishLoginUrl);

export type VerifyOptions = {
  referralCode?: string | null;
};
export const useVerify = (options?: UseMutationOptions<unknown, BaseError, VerifyOptions>) =>
  useMutation<unknown, BaseError, VerifyOptions>({
    mutationFn: finishLogin,
    ...options,
  });

export type SmsVerification = {
  success: boolean;
  walletSmsUri: string;
};
export const smsVerificationUrl = '/api/citizen/v1/check/sms-agreement';
export const getSmsVerification = async () => {
  const { data } = await axios.get<SmsVerification>(smsVerificationUrl);
  return data;
};
export const useSmsVerification = (options?: UseDosiminMutationOptions<SmsVerification>) => {
  const login = useLogin();
  const { open } = useModal();
  const defaultErrorHandler = useDefaultErrorHandler();

  const onError = useCallback(
    (error) => {
      switch (error.message) {
        case 'UNAUTHORIZED':
          login();
          break;
        case 'SMS_NOT_VERIFIED':
          open(<SmsVerificationPopup walletSmsUri={error.payload as string} />);
          break;
        default:
          defaultErrorHandler();
          break;
      }
    },
    [defaultErrorHandler, login, open]
  );
  return useMutation<SmsVerification>(
    async () => {
      const data = await getSmsVerification();
      const { success, walletSmsUri } = data;
      if (!success && walletSmsUri) {
        throw new BaseError('SMS_NOT_VERIFIED', walletSmsUri);
      }
      return data;
    },
    { onError, ...options }
  );
};
export const returnTo = (to = localStorage.getItem('RETURN_TARGET_URI') || '/') => {
  const target = window.opener || window;

  localStorage.removeItem('RETURN_TARGET_URI');
  target.location.replace(to);

  if (window.opener) {
    window.close();
  }
};
